// Maps Netlify build environment variables onto Faro `app` meta (→ OTLP resource
// attributes) plus session attributes. Pure and host-agnostic: the caller passes
// the env object (e.g. injected via a Vite `define` from `process.env` at build
// time, since Netlify build vars are NOT `VITE_`-prefixed and never reach
// `import.meta.env`). Opt-in — call it and spread the result into `initFaro`.
// Netlify vars: https://docs.netlify.com/configure-builds/environment-variables/
export interface NetlifyDeployMeta {
  // Full commit SHA → Faro `app.gitHash` (OTLP resource attribute).
  gitHash?: string;
  // Netlify site name → Faro `app.namespace` → `service.namespace` (canonical label).
  namespace?: string;
  // Unique deploy id → Faro `app.bundleId` (OTLP resource attribute).
  bundleId?: string;
  // Deployment platform marker (e.g. "netlify") → extra resource attribute on the
  // Faro app meta. Enables filtering traces by runtime platform in Grafana.
  runtime?: 'netlify';
  // Non-resource deploy metadata (context, branch, build id, deploy/review url).
  // Rides every event as a Faro session attribute — searchable, but a log-record
  // attribute, so NOT promoted to a label by the Alloy `zendos_labels` transform.
  sessionAttributes?: Record<string, string>;
}

export function netlifyDeployMeta(env: Record<string, string | undefined>): NetlifyDeployMeta {
  const sessionAttributes = Object.entries({
    'netlify.context': env.CONTEXT,
    'netlify.branch': env.BRANCH,
    'netlify.build_id': env.BUILD_ID,
    'netlify.deploy_url': env.DEPLOY_URL,
    'netlify.review_id': env.REVIEW_ID
  }).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});

  // Detect Netlify build context: any of the primary Netlify env vars present.
  const isNetlify = Boolean(env.CONTEXT || env.DEPLOY_ID || env.COMMIT_REF || env.SITE_NAME);

  return {
    gitHash: env.COMMIT_REF,
    namespace: env.SITE_NAME,
    bundleId: env.DEPLOY_ID,
    runtime: isNetlify ? 'netlify' : undefined,
    sessionAttributes: Object.keys(sessionAttributes).length > 0 ? sessionAttributes : undefined
  };
}
